import { cn } from '@/lib/cn';

import { SpartanLogo } from './SpartanLogo';

/**
 * Composición de la animación de entrada.
 *
 * En el centro va la ilustración de la marca —el guerrero con la lanza y el
 * escudo— y alrededor la escenografía que no cabe en un icono: aro de
 * remaches girando, corona de laurel, brasas y un destello de espada que cruza
 * la escena.
 *
 * El guerrero entra CON PESO: aparece grande y desenfocado, cae hasta su sitio
 * y en el impacto se sacude la pantalla. Es un recurso viejo de cine y
 * videojuego, y funciona porque el ojo lee la desaceleración brusca como
 * masa. Sin ese frenazo, la misma animación se siente como una diapositiva.
 *
 * Cada capa lleva su clase para poder escalonar la entrada: primero el halo,
 * luego el aro y las brasas, después el guerrero con su impacto, y al final el
 * laurel y el destello.
 */

interface SpartanWarriorProps {
  className?: string;
  /** Escalona la entrada de cada pieza. Sin esto, se dibuja ya montado. */
  animated?: boolean;
  title?: string;
}

export function SpartanWarrior({ className, animated, title }: SpartanWarriorProps) {
  return (
    <div
      className={cn('relative grid place-items-center', className)}
      role={title ? 'img' : 'presentation'}
      aria-label={title}
      aria-hidden={title ? undefined : true}
    >
      {/* --- Escenografía -------------------------------------------------- */}
      <svg viewBox="0 0 300 300" className="absolute inset-0 h-full w-full overflow-visible">
        <defs>
          <radialGradient id="cp-w-halo" cx="0.5" cy="0.5" r="0.5">
            <stop offset="0%" stopColor="rgb(var(--cp-crimson))" stopOpacity="0.5" />
            <stop offset="70%" stopColor="rgb(var(--cp-crimson))" stopOpacity="0.14" />
            <stop offset="100%" stopColor="rgb(var(--cp-crimson))" stopOpacity="0" />
          </radialGradient>
        </defs>

        <circle
          cx="150"
          cy="150"
          r="148"
          fill="url(#cp-w-halo)"
          className={animated ? 'animate-warrior-halo' : undefined}
        />

        {/* Aro de remaches, girando muy despacio */}
        <g
          className={animated ? 'animate-warrior-shield' : undefined}
          style={{ transformOrigin: '150px 150px' }}
        >
          <circle
            cx="150"
            cy="150"
            r="138"
            fill="none"
            stroke="rgb(var(--cp-gold))"
            strokeWidth="1.5"
            opacity="0.32"
          />
          <g
            className={animated ? 'animate-ring-turn' : undefined}
            style={{ transformOrigin: '150px 150px' }}
          >
            {Array.from({ length: 36 }, (_, i) => {
              const angle = (i / 36) * Math.PI * 2;
              return (
                <circle
                  key={i}
                  cx={150 + Math.cos(angle) * 138}
                  cy={150 + Math.sin(angle) * 138}
                  r={i % 3 === 0 ? 2.6 : 1.4}
                  fill="rgb(var(--cp-gold))"
                  opacity={i % 3 === 0 ? 0.8 : 0.45}
                />
              );
            })}
          </g>
        </g>

        {/* Corona de laurel abrazando la parte baja */}
        <g
          className={animated ? 'animate-warrior-crest' : undefined}
          style={{ transformOrigin: '150px 200px' }}
        >
          <LaurelHalf />
          <g transform="translate(300,0) scale(-1,1)">
            <LaurelHalf />
          </g>
        </g>

        {animated && (
          <g className="animate-warrior-embers">
            {EMBERS.map((e, i) => (
              <circle
                key={i}
                cx={e.x}
                cy={e.y}
                r={e.r}
                fill="rgb(var(--cp-gold))"
                className="animate-ember-rise"
                style={{ animationDelay: `${e.delay}ms`, animationDuration: `${e.duration}ms` }}
              />
            ))}
          </g>
        )}
      </svg>

      {/* --- El guerrero ---------------------------------------------------- */}
      <div
        className={cn('relative w-[86%]', animated && 'animate-warrior-slam')}
        style={{ transformOrigin: 'center' }}
      >
        {/*
          `h-auto`, no `h-full`. El contenedor tiene ancho pero su altura la
          decide el contenido; pedirle a la imagen el 100% de esa altura crea
          una dependencia circular y el navegador la resuelve con 0. Con el SVG
          anterior no pasaba porque un viewBox aporta proporcion intrinseca.
        */}
        <SpartanLogo
          variant="full"
          sizes="min(45vmin, 396px)"
          className="h-auto w-full drop-shadow-[0_14px_40px_rgb(0_0_0/0.65)]"
        />
      </div>

      {/* --- Destello de espada, después del impacto ------------------------ */}
      {animated && (
        <span className="pointer-events-none absolute inset-0 overflow-hidden">
          <span className="absolute -inset-y-1/2 left-0 w-1/3 animate-blade-flash [background:linear-gradient(100deg,transparent,rgb(var(--cp-marble)/0.5),transparent)]" />
        </span>
      )}
    </div>
  );
}

/** Media corona de laurel; la otra mitad es su espejo. */
function LaurelHalf() {
  return (
    <g stroke="rgb(var(--cp-gold))" fill="rgb(var(--cp-gold))" opacity="0.7">
      <path
        d="M146 292 C104 284, 70 254, 58 210"
        fill="none"
        strokeWidth="2.5"
        strokeLinecap="round"
      />
      {[
        'M58 210 c-11-5 -18 3 -14 11 9 2 15-3 14-11z',
        'M64 230 c-11-5 -18 3 -14 11 9 2 15-3 14-11z',
        'M76 250 c-10-6 -18 1 -15 10 9 2 16-2 15-10z',
        'M92 266 c-9-7 -18 0 -16 8 9 3 16-1 16-8z',
        'M114 280 c-8-8 -17-2 -16 6 9 4 16 1 16-6z',
      ].map((d) => (
        <path key={d} d={d} stroke="none" />
      ))}
    </g>
  );
}

/** Brasas: posición, tamaño y ritmo fijos, para que no bailen en cada render. */
const EMBERS = [
  { x: 54, y: 236, r: 2.4, delay: 0, duration: 2600 },
  { x: 246, y: 252, r: 1.8, delay: 420, duration: 3000 },
  { x: 80, y: 274, r: 1.4, delay: 900, duration: 2400 },
  { x: 220, y: 224, r: 2.1, delay: 1300, duration: 2800 },
  { x: 132, y: 292, r: 1.6, delay: 700, duration: 3200 },
  { x: 176, y: 286, r: 1.2, delay: 1700, duration: 2500 },
  { x: 34, y: 196, r: 1.7, delay: 2100, duration: 2900 },
  { x: 268, y: 186, r: 1.5, delay: 1100, duration: 3100 },
];
